import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ApiCallingService } from "../../services/api-calling.service";
import { ConfigApi } from "../../config-api";
import { ToastrService } from "ngx-toastr";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import * as mapboxgl from "mapbox-gl";
import { environment } from "../../../environments/environment";
import { HttpParams } from "@angular/common/http";

@Component({
  selector: "app-beach-booking-detail",
  templateUrl: "./beach-booking-detail.component.html",
  styleUrls: ["./beach-booking-detail.component.css"],
})
export class BeachBookingDetailComponent implements OnInit {
  details: any;
  bookingId!: string;
  @ViewChild("slotDetailsModal") slotDetailsModal: any;
  @ViewChild("mapModal") mapModal: any;
  @ViewChild("mapContainer", { read: ElementRef }) mapContainer!: ElementRef;
  slotDetails: any;
  map!: mapboxgl.Map;
  style = "mapbox://styles/mapbox/streets-v11";
  accessToken = environment.mapbox.accessToken;
  lat: number = 0;
  lng: number = 0;
  private gridCache: Map<string, any[][]> = new Map();

  constructor(
    private route: ActivatedRoute,
    private apiCallingService: ApiCallingService,
    private toastr: ToastrService,
    private modalService: NgbModal
  ) {
    (mapboxgl as any).accessToken = this.accessToken;
  }

  ngOnInit(): void {
    this.bookingId = this.route.snapshot.paramMap.get("id") || "";
    this.loadBookingDetails();
  }

  loadBookingDetails(): void {
    const url = ConfigApi.URLS.SPOT_BOOKING.DETAILS + "/" + this.bookingId;
    this.apiCallingService.callAPI({}, "GET", url).subscribe({
      next: (res) => {
        if (this.apiCallingService.handleSuccess(res)) {
          this.details = res.data;
          this.gridCache.clear();
          if (this.details?.zones) {
            this.details.zones.forEach((zone: any, index: number) => {
              zone.gridLayout = this.createGridLayout(zone);
              zone.zoneIndex = index;
            });
          }
          if (this.details?.location?.coordinates) {
            [this.lng, this.lat] = this.details.location.coordinates;
          }
        } else {
          this.toastr.error(res.msg, "Error");
        }
      },
      error: (err) => {
        this.toastr.error(
          err.error?.msg || "Failed to load booking details",
          "Error"
        );
      },
    });
  }

  getAvailableSlots(slots: any[]): number {
    return slots?.filter((slot) => slot.status === "available").length || 0;
  }

  getBookedSlots(slots: any[]): number {
    return slots?.filter((slot) => slot.status === "booked").length || 0;
  }

  slotBooking(slot: any) {
    this.modalService.open(this.slotDetailsModal, {
      centered: true,
      backdrop: "static",
    });
    this.slotDetails = slot;
  }

  openMapModal(): void {
    const modalRef = this.modalService.open(this.mapModal, {
      centered: true,
      size: "lg",
    });

    const container = document.createElement("div");
    container.style.width = "100%";
    container.style.height = "300px";
    container.style.borderRadius = "8px";

    const modalBody = document.querySelector(".modal-body");
    if (modalBody) {
      modalBody.appendChild(container);

      setTimeout(() => {
        this.initializeMap(container);
      }, 0);
    }

    modalRef.result.then(
      () => this.cleanUpMap(),
      () => this.cleanUpMap()
    );
  }
  private cleanUpMap(): void {
    if (this.map) {
      this.map.remove();
      this.map = null as any;
    }
  }
  initializeMap(container: HTMLElement): void {
    this.map = new mapboxgl.Map({
      container: container,
      style: this.style,
      center: [this.lng, this.lat],
      zoom: 8,
    });

    new mapboxgl.Marker()
      .setLngLat([this.lng, this.lat])
      .setPopup(
        new mapboxgl.Popup().setHTML(
          `<h5>${this.details?.title || "Location"}</h5><p>${
            this.details?.address || ""
          }</p>`
        )
      )
      .addTo(this.map);

    this.map.addControl(new mapboxgl.NavigationControl());

    setTimeout(() => {
      this.map.resize();
    }, 100);
  }

  parseBlockedDates(dateRange: string): string {
    try {
      const cleanedString = dateRange.replace(/^\"|\"$/g, "");
      const dates = cleanedString;
      if (Array.isArray(dates) && dates.length >= 2) {
        return `${dates[0]} to ${dates[1]}`;
      }
      if (Array.isArray(dates) && dates.length === 1) {
        return dates[0];
      }
      return dateRange;
    } catch (error) {
      console.warn("Error parsing blocked dates:", error);
      return dateRange;
    }
  }

  toggleSeat(slot: any) {
    let isAvailable = slot.status === "blocked";
    let newStatus = slot.status === "blocked" ? "available" : "blocked";

    let body = new HttpParams()
      .set("isAvailable", isAvailable)
      .set("status", newStatus);

    const url = ConfigApi.URLS.SPOT_BOOKING.BLOCK_SEAT + "/" + slot._id;
    let method = "PUT";

    this.apiCallingService.callAPI(body, method, url).subscribe(
      (res) => {
        if (this.apiCallingService.handleSuccess(res)) {
          this.toastr.success(res.msg);
          this.slotDetails.status = newStatus;
          this.modalService.dismissAll();
          this.ngOnInit();
        } else {
          this.toastr.error(res.msg, "Error");
        }
      },
      (err) => {
        this.toastr.error(err.error?.msg || "Failed to update seat", "Error");
      }
    );
  }

  getSlot(rowIndex: number, colIndex: number, zone: any) {
    const index = rowIndex * zone.numOfColumns + colIndex;
    return zone.slots[index] || null;
  }

  NumberFormat(value: any) {
    return Number(value);
  }

  createGridLayout(zone: any): any[][] {
    const { numOfRows, numOfColumns, slots } = zone;

    const cacheKey = `${zone.zoneName || "zone"}_${numOfRows}_${numOfColumns}_${
      slots?.length || 0
    }`;

    if (this.gridCache.has(cacheKey)) {
      return this.gridCache.get(cacheKey)!;
    }

    const grid: any[][] = [];

    for (let row = 0; row < numOfRows; row++) {
      grid[row] = [];
      for (let col = 0; col < numOfColumns; col++) {
        grid[row][col] = null;
      }
    }

    if (slots && Array.isArray(slots)) {
      slots.forEach((slot: any) => {
        if (slot?.position && typeof slot.position === "object") {
          const { row, column } = slot.position;
          if (
            row >= 0 &&
            row < numOfRows &&
            column >= 0 &&
            column < numOfColumns
          ) {
            grid[row][column] = slot;
          }
        }
      });
    }

    this.gridCache.set(cacheKey, grid);
    return grid;
  }
  getZoneGrid(zone: any): any[][] {
    return zone.gridLayout || this.createGridLayout(zone);
  }
  getSubcategoryName(id: string) {
    let subCategories =
      this.details &&
      this.details.categoryId &&
      this.details.categoryId.subCategories;
    let subCategory = subCategories?.find((sub: any) => sub._id === id);

    return subCategory?.name;
  }
}
